"use client";

import React, { useState, useEffect, useRef } from "react";
import { Image } from "react-bootstrap";
import { gsap } from "gsap";

const STATIC_IMAGES_ROW1 = [
  "/assets/img/codeqlik_partner1.png",
  "/assets/img/codeqlik_partner10.PNG",
  "/assets/img/codeqlik_partner11.png",
  "/assets/img/codeqlik_partner12-removebg-preview.png",
  "/assets/img/codeqlik_partner2.png",
  "/assets/img/codeqlik_partner3.png",
  "/assets/img/codeqlik_partner4.png",
  "/assets/img/codeqlik_partner5-removebg-preview.png",
  "/assets/img/codeqlik_partner6.png",
  "/assets/img/codeqlik_partner7.jpeg",
  "/assets/img/codeqlik_partner8-removebg-preview.png",
];

const STATIC_IMAGES_ROW2 = [
  "/assets/img/logo-14.jpeg",
  "/assets/img/logo2.jpeg",
  "/assets/img/logo-3.jpeg",
  "/assets/img/logo-5.jpeg",
  "/assets/img/logo-4.jpeg",
  "/assets/img/logo13.jpeg",
  "/assets/img/codeqlik_partner1.png",
];

const BrandsSlider = ({ title = "Trusted by Leading Brands", subtitle = "Brands Collaboration", className = "" }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useFallback, setUseFallback] = useState(false);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);

  useEffect(() => {
    const fetchBrands = async () => {
      const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
      try {
        const res = await fetch(`${apiUrl}/api/brand/public?limit=50`);
        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();

        const allLogos = data.data.brands.reduce((acc, brand) => {
          const rawLogos = brand.images || brand.logos || [];
          if (Array.isArray(rawLogos)) {
            const formattedLogos = rawLogos.map(logo => {
              const logoStr = typeof logo === 'string' ? logo : (logo?.url || '');
              if (!logoStr) return '';

              let finalUrl = logoStr;
              if (logoStr.startsWith('http') && !logoStr.includes('/uploads/')) {
                finalUrl = logoStr;
              } else {
                let cleanPath = logoStr;
                if (logoStr.includes('/uploads/')) {
                  cleanPath = logoStr.substring(logoStr.indexOf('/uploads/'));
                }

                const path = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;

                if (path.startsWith('/uploads/')) {
                  finalUrl = `${apiUrl}${path}`;
                } else {
                  finalUrl = path;
                }
              }

              // Apply Cloudinary optimization and scaling for logos (mobile optimized)
              if (finalUrl.includes("res.cloudinary.com") && finalUrl.includes("/image/upload/")) {
                if (!finalUrl.includes("f_auto") && !finalUrl.includes("q_auto")) {
                  finalUrl = finalUrl.replace("/image/upload/", "/image/upload/f_auto,q_auto:eco,w_100,c_fit/");
                }
              }

              return finalUrl;
            }).filter(Boolean);
            return [...acc, ...formattedLogos];
          }
          return acc;
        }, []);

        if (allLogos.length > 0) {
          setBrands(allLogos);
          setUseFallback(false);
        } else {
          setBrands([]);
          setUseFallback(true);
        }
      } catch (error) {
        console.warn("Error fetching brands (API unreachable), using static fallback:", error);
        setBrands([]);
        setUseFallback(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  // Split brands into two rows (only render fallback if API is unreachable/fails)
  const row1Base = !loading && !useFallback && brands.length > 0 ? brands.slice(0, Math.ceil(brands.length / 2)) : (useFallback ? STATIC_IMAGES_ROW1 : []);
  const row2Base = !loading && !useFallback && brands.length > 0 ? brands.slice(Math.ceil(brands.length / 2)) : (useFallback ? STATIC_IMAGES_ROW2 : []);

  // Duplicate for seamless infinite loop directly in React
  const row1Images = row1Base.length > 0 ? [...row1Base, ...row1Base] : [];
  const row2Images = row2Base.length > 0 ? [...row2Base, ...row2Base] : [];

  useEffect(() => {
    if (loading || row1Images.length === 0) return;

    let anim1, anim2;
    const startScroll = () => {
      const initRow = (ref, speed, direction) => {
        if (!ref.current) return null;
        const inner = ref.current.querySelector(".slider-inner");
        if (!inner) return null;

        const totalWidth = inner.scrollWidth / 2;
        if (totalWidth <= 0) return null;

        gsap.set(inner, { x: direction === "left" ? "0%" : `-${totalWidth}px` });

        return gsap.to(inner, {
          x: direction === "left" ? `-${totalWidth}px` : "0%",
          duration: speed,
          ease: "linear",
          repeat: -1,
        });
      };

      anim1 = initRow(row1Ref, 30, "left");
      anim2 = initRow(row2Ref, 30, "right");
    };

    const rafId = requestAnimationFrame(() => {
      startScroll();
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (anim1) anim1.kill();
      if (anim2) anim2.kill();
    };
  }, [loading, brands, useFallback]);

  return (
    <div className={`container-fluid p-3 ${className}`}>
      <div className="text-center mb-5" data-aos="fade-up" data-aos-delay="0">
        <span className="brands">{subtitle}</span>
        <h2 className="mt-3 brand-trust">{title}</h2>
      </div>
      <div className="slider-container-home">
        <div className="slider-row slider-row-left" ref={row1Ref}>
          <div className="slider-inner">
            {row1Images.map((src, index) => (
              <div className="slider-card" key={`r1-${index}`}>
                <Image src={src} alt="Trusted Partner Brand Logo" />
              </div>
            ))}
          </div>
        </div>
        <div className="slider-row slider-row-right" ref={row2Ref}>
          <div className="slider-inner">
            {row2Images.map((src, index) => (
              <div className="slider-card" key={`r2-${index}`}>
                <Image src={src} alt="Trusted Partner Brand Logo" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandsSlider;
